import { Injectable, Inject } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import {
  APP_CONFIG,
  ConfigSettings,
  QUERY_PARAMS,
  ROUTES,
} from '@shared/config';
import { findProtectedRouteMatch, getProtectedRoutes } from '@msal/msal.config';
import { MsalTokenProviderService } from '@services/msal-token-provider.service';
import { switchMap, catchError } from 'rxjs/operators';
import { Router } from '@angular/router';

@Injectable()
export class MsalTokenInterceptor implements HttpInterceptor {
  private readonly settings: ConfigSettings;
  constructor(
    @Inject(APP_CONFIG) settings: ConfigSettings,
    private msalTokenProviderService: MsalTokenProviderService,
    private router: Router
  ) {
    this.settings = settings;
  }
  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const protectedRoutes = getProtectedRoutes(this.settings);
    if (!protectedRoutes || !protectedRoutes.length) {
      return next.handle(request);
    }

    const matchedRoute = findProtectedRouteMatch(protectedRoutes, request.url);
    if (matchedRoute) {
      const scopesList = matchedRoute?.scopes || [];
      const useHomeTenant = matchedRoute?.useHomeTenant || false;
      if (scopesList && scopesList.length) {
        return this.buildTokenRequest(request, next, scopesList, useHomeTenant);
      }
    }

    return next.handle(request);
  }

  private buildTokenRequest(
    request: HttpRequest<any>,
    next: HttpHandler,
    scopes: string[],
    useHomeTenant: boolean = false
  ): Observable<HttpEvent<any>> {
    const token$ = this.msalTokenProviderService
      .getMsalAccessToken(scopes, false, null, useHomeTenant)
      .pipe(
        catchError(error => {
          console.error('MSAL token acquisition failed', error);
          this.router.navigate([ROUTES.login], {
            queryParams: { [QUERY_PARAMS.returnUrl]: this.router.url },
            replaceUrl: true,
          });
          return throwError(() => error);
        })
      );

    return token$.pipe(
      switchMap(msalToken => {
        const newRequest = request.clone({
          setHeaders: {
            Authorization: `Bearer ${msalToken.accessToken}`,
          },
        });
        return next.handle(newRequest);
      })
    );
  }
}
